import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { PhoneCall, CheckCircle2, RefreshCw, Stethoscope, Heart, Activity, Thermometer, Droplets, Weight, Ruler, ClipboardList } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import ConsultationPanel from "@/components/ConsultationPanel";

export default function DoctorConsultation() {
  const { branch, user } = useAuth();
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [openConsult, setOpenConsult] = useState(null);
  const [doctorFilter, setDoctorFilter] = useState(
    user.username.startsWith("dr.") ? user.username : ""
  );
  const [doctors, setDoctors] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { branch_code: branch.code };
      if (doctorFilter) params.doctor_username = doctorFilter;
      const [qRes, docsRes] = await Promise.all([
        api.get("/queue/doctor", { params }),
        api.get("/doctors", { params: { branch_code: branch.code } }),
      ]);
      setQueue(qRes.data);
      setDoctors(docsRes.data);
    } catch (err) {
      toast.error(formatApiError(err, "Could not load consultation queue"));
    } finally {
      setLoading(false);
    }
  }, [branch?.code, doctorFilter]);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const call = async (p) => {
    setBusyId(p.id);
    try {
      await api.post(`/patients/${p.id}/call`);
      toast.success(`Calling ${p.op_token} · ${p.name}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not call patient"));
    } finally {
      setBusyId(null);
    }
  };

  const complete = async (p) => {
    setBusyId(p.id);
    try {
      await api.post(`/patients/${p.id}/complete`);
      toast.success(`Consultation complete · ${p.op_token}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not complete"));
    } finally {
      setBusyId(null);
    }
  };

  const inConsult = queue.find(
    (p) =>
      p.status === "in_consultation" &&
      (!doctorFilter || p.assigned_doctor === doctorFilter)
  );
  const waiting = queue.filter(
    (p) =>
      p.status === "awaiting_doctor" &&
      (!doctorFilter || p.assigned_doctor === doctorFilter)
  );
  const next = waiting[0];

  const isShared = user.username === "doclog";
  const isAllDoctorsView = isShared && !doctorFilter;

  return (
    <div className="max-w-6xl mx-auto animate-fade-up" data-testid="doctor-consultation-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-[#5C5652] mb-3 ornament-dot">
            Consultation · {branch.name}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-forest mb-1">
            Doctor&apos;s queue
          </h1>
          <p className="text-[#5C5652]">
            One-tap &ldquo;Call Next Patient&rdquo; drives the public queue board.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="doctor-refresh"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-sand bg-white hover:bg-[#F9F6F0] transition-colors text-sm font-semibold"
        >
          <RefreshCw className="h-4 w-4" strokeWidth={1.75} />
          Refresh
        </button>
      </div>

      {isShared && (
        <div className="mb-6 p-4 rounded-lg bg-white border border-sand">
          <div className="text-[10px] uppercase tracking-wider text-[#5C5652] mb-2">
            Viewing queue for
          </div>
          <div className="flex flex-wrap gap-2" data-testid="doctor-filter-picker">
            <button
              onClick={() => setDoctorFilter("")}
              data-testid="doctor-filter-all"
              className={`h-9 px-3 rounded-lg border text-sm font-semibold transition-colors ${
                doctorFilter === "" ? "bg-forest text-white border-forest" : "border-sand hover:bg-[#F9F6F0]"
              }`}
            >
              All doctors
            </button>
            {doctors.map((d) => (
              <button
                key={d.username}
                onClick={() => setDoctorFilter(d.username)}
                data-testid={`doctor-filter-${d.username}`}
                className={`h-9 px-3 rounded-lg border text-sm font-semibold transition-colors ${
                  doctorFilter === d.username
                    ? "bg-forest text-white border-forest"
                    : "border-sand hover:bg-[#F9F6F0]"
                }`}
              >
                {d.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Now Serving */}
      <div className="mb-6 rounded-lg border border-sand overflow-hidden">
        <div className="bg-forest text-white px-6 py-4 flex items-center gap-3">
          <Stethoscope className="h-5 w-5" strokeWidth={1.5} />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.22em] opacity-80">Now consulting</div>
            <div className="font-display text-lg">
              {inConsult ? `${inConsult.op_token} · ${inConsult.name}` : "No one — call the next patient"}
            </div>
          </div>
          {inConsult && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setOpenConsult(inConsult)}
                data-testid="open-consultation-button"
                className="h-10 px-4 rounded-lg bg-[#E5A93D] text-[#2A2624] font-semibold hover:opacity-90 transition-opacity inline-flex items-center gap-2"
              >
                <ClipboardList className="h-4 w-4" strokeWidth={1.75} />
                Consult now
              </button>
              <button
                onClick={() => complete(inConsult)}
                disabled={busyId === inConsult.id}
                data-testid="complete-current-button"
                className="h-10 px-4 rounded-lg bg-white text-forest font-semibold hover:bg-[#F9F6F0] transition-colors inline-flex items-center gap-2 disabled:opacity-60"
              >
                <CheckCircle2 className="h-4 w-4" strokeWidth={1.75} />
                Skip &amp; complete
              </button>
            </div>
          )}
        </div>
        {inConsult && inConsult.vitals && (
          <div className="bg-white px-6 py-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <VitalChip icon={Heart} label="BP" value={
              inConsult.vitals.bp_systolic && inConsult.vitals.bp_diastolic
                ? `${inConsult.vitals.bp_systolic}/${inConsult.vitals.bp_diastolic}` : "—"
            } />
            <VitalChip icon={Activity} label="Pulse" value={inConsult.vitals.pulse ?? "—"} />
            <VitalChip icon={Thermometer} label="Temp" value={inConsult.vitals.temp_c ? `${inConsult.vitals.temp_c}°C` : "—"} />
            <VitalChip icon={Droplets} label="SpO₂" value={inConsult.vitals.spo2 ? `${inConsult.vitals.spo2}%` : "—"} />
            <VitalChip icon={Weight} label="Weight" value={inConsult.vitals.weight_kg ? `${inConsult.vitals.weight_kg}kg` : "—"} />
            <VitalChip icon={Ruler} label="Height" value={inConsult.vitals.height_cm ? `${inConsult.vitals.height_cm}cm` : "—"} />
            {inConsult.complaint && (
              <div className="col-span-2 sm:col-span-4 text-sm text-[#5C5652]">
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#5C5652] mr-2">Complaint</span>
                <span className="text-[#2A2624]">{inConsult.complaint}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Next up */}
      {next && (
        <div className="mb-8 p-6 rounded-lg bg-white border border-sand" data-testid="next-patient-card">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5652] mb-3">Up next</div>
          <div className="flex flex-wrap items-center gap-4">
            <div className="font-display text-5xl text-forest leading-none">{next.op_token}</div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-lg text-[#2A2624]">{next.name}</div>
              <div className="text-sm text-[#5C5652]">
                {next.age} · {next.gender} · assigned to {next.assigned_doctor_name}
              </div>
            </div>
            <button
              onClick={() => call(next)}
              disabled={busyId === next.id || !!inConsult || isAllDoctorsView}
              data-testid="call-next-button"
              title={
                isAllDoctorsView
                  ? "Pick a specific doctor above to call their patient"
                  : inConsult
                    ? "Complete the current patient first"
                    : "Call this patient"
              }
              className="h-12 px-6 rounded-lg bg-forest text-white font-semibold text-base hover:bg-forest-hover transition-colors inline-flex items-center gap-2 disabled:opacity-50"
            >
              <PhoneCall className="h-5 w-5" strokeWidth={1.75} />
              Call patient
            </button>
          </div>
          {(inConsult || isAllDoctorsView) && (
            <div className="mt-4 text-xs text-[#5C5652] pl-1">
              {isAllDoctorsView
                ? "Pick a specific doctor above to call their patient."
                : "Complete the current consultation before calling the next patient."}
            </div>
          )}
        </div>
      )}

      {/* Waiting list */}
      <div>
        <div className="text-xs uppercase tracking-[0.28em] text-[#5C5652] mb-4 ornament-dot">
          Waiting queue · {waiting.length}
        </div>
        <div className="bg-white border border-sand rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-8 text-sm text-[#5C5652]">Loading…</div>
          ) : waiting.length === 0 ? (
            <div className="p-10 text-center">
              <div className="font-display text-forest text-xl mb-1">No one is waiting</div>
              <p className="text-sm text-[#5C5652]">
                The nurse will send patients here after recording their vitals.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-[#E8E3D9]" data-testid="doctor-waiting-list">
              {waiting.map((p, i) => (
                <li key={p.id} className="flex items-center gap-4 px-6 py-4">
                  <div className="h-9 w-9 rounded-full bg-[#1A4331]/10 text-forest flex items-center justify-center font-bold text-sm">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#2A2624]">{p.name}</div>
                    <div className="text-xs text-[#5C5652]">
                      Token {p.op_token} · {p.assigned_doctor_name}
                    </div>
                  </div>
                  {i === 0 && !inConsult && (
                    <button
                      onClick={() => call(p)}
                      disabled={busyId === p.id}
                      data-testid={`call-${p.mrn}`}
                      className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-forest text-white text-sm font-semibold hover:bg-forest-hover transition-colors disabled:opacity-60"
                    >
                      <PhoneCall className="h-3.5 w-3.5" strokeWidth={1.75} />
                      Call
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {openConsult && (
        <ConsultationPanel
          patient={openConsult}
          onClose={() => setOpenConsult(null)}
          onSubmitted={() => {
            setOpenConsult(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function VitalChip({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2 p-3 rounded-lg bg-[#F9F6F0] border border-sand">
      <Icon className="h-4 w-4 text-forest" strokeWidth={1.5} />
      <div>
        <div className="text-[9px] uppercase tracking-wider text-[#5C5652] leading-tight">{label}</div>
        <div className="font-semibold text-[#2A2624]">{value}</div>
      </div>
    </div>
  );
}
